<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import type { ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api-models";
	import type { SingleMachineField } from "@shared/types";
	import type { SimplePoint } from "@ptx-autonomous/libs/api-models";
	import type { Polygon, MultiPolygon, MultiLineString } from "geojson";
	import type { PreviewMarker } from "@shared/types";
	import { useMissionPreview } from "@shared/composables";
	import ButtonSecondary from "../ButtonSecondary.vue";
	import ButtonPrimary from "../ButtonPrimary.vue";
	import FloatingTooltip from "@/components/FloatingTooltip.vue";

	const { selectedImplement, plannedPath, targetSpeed, turningSpeed } =
		defineProps<{
			selectedField: SingleMachineField;
			selectedImplement: ImplementV2Model;
			plannedPath: SimplePoint[];
			targetSpeed: number;
			turningSpeed: number;
		}>();

	const emits = defineEmits<{
		missionPreviewUpdate: [
			data: {
				coverage: Polygon | MultiPolygon | null;
				drivenPath: MultiLineString;
				previewMarker: PreviewMarker;
			},
		];
	}>();

	const implementWidth = computed(
		() => selectedImplement.Sections?.[0]?.ElementWorkingWidth || 0,
	);

	const {
		playbackSpeed,
		isStarted,
		isPaused,
		isComplete,
		pathProgress,
		previewMarker,
		drivenPath,
		coverage,
		play,
		stop,
	} = useMissionPreview({
		plannedPath: computed(() => plannedPath),
		targetSpeed,
		turningSpeed,
		implementWidth: implementWidth.value,
	});

	// Emit map components data to parent when they change
	watch(
		[coverage, drivenPath, previewMarker],
		() => {
			emits("missionPreviewUpdate", {
				coverage: coverage.value,
				drivenPath: drivenPath.value,
				previewMarker: previewMarker.value,
			});
		},
		{ immediate: true },
	);

	const playButtonText = computed(() => {
		if (isComplete.value) return "Restart";
		if (isPaused.value) return "Resume";
		if (isStarted.value && !isPaused.value) return "Pause";
		return "Preview";
	});

	const playButtonIcon = computed(() => {
		if (isStarted.value && !isPaused.value) {
			// Pause icon
			return `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
    </svg>`;
		}
		// Play icon
		return `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z"/>
  </svg>`;
	});

	const stopButtonIcon = `<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
  <path d="M6 6h12v12H6z"/>
</svg>`;

	const isExpanded = ref(true);

	function toggleExpanded() {
		isExpanded.value = !isExpanded.value;
	}
</script>

<template>
	<div class="space-y-4">
		<div
			class="bg-green-50 border border-green-200 rounded-lg overflow-hidden"
		>
			<div
				class="px-4 py-3 cursor-pointer hover:bg-green-100 transition-colors"
				:title="
					isExpanded
						? 'Collapse mission preview'
						: 'Expand mission preview'
				"
				@click="toggleExpanded"
			>
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-2">
						<h3 class="title text-green-800 font-semibold">
							Mission Preview
						</h3>
						<FloatingTooltip trigger="click">
							<template #trigger>
								<button
									class="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-xs hover:bg-green-800 transition-colors"
									type="button"
								>
									i
								</button>
							</template>

							<div class="space-y-1 text-left whitespace-nowrap">
								<div>• Click Preview to start animation</div>
								<div>• Purple line shows completed path</div>
								<div>• Green area shows field coverage</div>
								<div>• Machine marker shows position</div>
							</div>
						</FloatingTooltip>
					</div>
				</div>
			</div>

			<!-- Collapsible content -->
			<Transition
				enter-active-class="transition-all duration-100 ease-out"
				leave-active-class="transition-all duration-100 ease-in"
				enter-from-class="opacity-0 max-h-0"
				enter-to-class="opacity-100 max-h-[70vh]"
				leave-from-class="opacity-100 max-h-[70vh]"
				leave-to-class="opacity-0 max-h-0"
			>
				<div
					v-if="isExpanded"
					class="overflow-hidden"
				>
					<div class="px-4 pb-3 space-y-3">
						<div class="flex gap-3 items-end">
							<!-- Play/Pause/Stop Controls -->
							<div class="flex gap-2">
								<ButtonPrimary
									@click="play"
									class="flex items-center justify-center gap-1.5 px-3 py-2 font-medium text-sm w-[96px]"
								>
									<span v-html="playButtonIcon"></span>
									<span>{{ playButtonText }}</span>
								</ButtonPrimary>
								<ButtonSecondary
									@click="stop"
									class="flex items-center gap-1 px-3 py-2 font-medium text-sm"
								>
									<span v-html="stopButtonIcon"></span>
									<span>Stop</span>
								</ButtonSecondary>
							</div>

							<!-- Speed Controls -->
							<div
								class="flex-1 bg-grey-100 rounded-lg px-2 py-1"
							>
								<label
									class="block text-xs font-medium text-grey-700 mb-0.5"
								>
									Speed
								</label>
								<div class="grid grid-cols-4 gap-1">
									<button
										v-for="speed in [
											'10',
											'100',
											'500',
											'1000',
										]"
										:key="speed"
										@click="playbackSpeed = speed"
										class="px-2 py-1 text-xs font-medium rounded-md transition-colors"
										:class="{
											'bg-white text-green-600 shadow-sm':
												playbackSpeed === speed,
											'text-grey-600 hover:text-grey-950':
												playbackSpeed !== speed,
										}"
									>
										{{ speed }}x
									</button>
								</div>
							</div>
						</div>

						<!-- Coverage Progress -->
						<div
							class="p-2 bg-white rounded-lg border border-green-800"
						>
							<div class="flex items-center justify-between mb-1">
								<h4 class="text-xs font-medium text-green-800">
									Path Progress
								</h4>
								<div
									class="text-xs font-semibold text-green-800"
								>
									{{ Math.round(pathProgress) }}% Complete
								</div>
							</div>
							<div class="w-full bg-grey-200 rounded-full h-1.5">
								<div
									class="bg-green-600 h-1.5 rounded-full"
									:style="{ width: `${pathProgress}%` }"
								></div>
							</div>
						</div>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>
